import React from "react";
import { Box, ButtonBase, styled, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useLocation, useNavigate, NavigateFunction } from "react-router-dom";
import { navigateToTop } from "../../../common/utils";
import { Span } from "./Typography";
import { navigations } from "./navigation";
import Ad from "./ad";
import { TOPBARHEIGHT } from "../../../common/constants";
import { AppContext, NavigationItem, Size } from "../../../types";

interface MultiLevelMenuProps {
    sidebarCompact?: boolean;
    setShowMobileSideBar?: (show: boolean) => void;
    context: AppContext;
    size: Size;
}

const NavItemButton = styled(ButtonBase)<{ active?: number }>(({ theme, active }) => ({
    height: 44,
    borderLeft: active ? `4px solid ${theme.palette.account.accentGradientEnd}` : `unset`,
    borderRadius: 0,
    marginBottom: 4,
    padding: `0 18px`,
    justifyContent: `flex-start`,
    transition: `all 0.15s ease`,
    ...(active && {
        color: `#666`,
        backgroundColor: `rgb(246, 247, 248)`,
    }),
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
}));

const StyledText = styled(Span)<{ compact?: number; active?: number }>(({ theme, compact, active }) => ({
    whiteSpace: `nowrap`,
    paddingLeft: `0.8rem`,
    transition: `all 0.15s ease`,
    fontSize: `16px`,
    fontWeight: 500,
    color: active ? theme.palette.account.accentGradientEnd : theme.palette.account.text.primary,
    ...(compact && {
        opacity: 0,
        width: 0,
    }),
}));

const BadgeValue = styled(Box)<{ compact?: number }>(({ compact, theme }) => ({
    color: `white`,
    fontSize: `16px`,
    fontWeight: 500,
    padding: `1px 6px`,
    overflow: `clip`,
    borderRadius: `300px`,
    backgroundColor: theme.palette.account.primary,
    display: compact ? `none` : `unset`,
})); // Common icon style

const iconStyle = (active: number, theme: Theme) => ({
    fontSize: 18,
    marginRight: `4px`,
    color: active ? theme.palette.account.primary : `text.secondary`,
}); // ---------------------------------------------------------------

// ---------------------------------------------------------------
const MultiLevelMenu = ({ sidebarCompact, setShowMobileSideBar, context, size }: MultiLevelMenuProps) => {
    const navigate: NavigateFunction = useNavigate();
    const theme = useTheme();
    const { pathname } = useLocation(); // handle active current page
    const activeRoute = (path: string, _theme?: Theme): number =>
        pathname === path || (pathname.startsWith(path) && path.split(`/`).length > 2) ? 1 : 0; // handle navigate to another route or page

    const handleNavigation = (path: string | string[]) => {
        if (setShowMobileSideBar) {
            setShowMobileSideBar(false);
        }
        navigateToTop(navigate, Array.isArray(path) ? path[0] : path);
    }; // ACTIVATE SIDEBAR COMPACT

    const COMPACT = sidebarCompact ? 1 : 0; //   RECURSIVE FUNCTION TO RENDER MULTI LEVEL MENU

    const renderLevels = (data: NavigationItem[]) => (
        <Box mr={4}>
            {context?.adminConfig?.mall?.ad_placements?.account_placement && (
                <Ad
                    size={size}
                    sx={{ marginBottom: `30px` }}
                    visual={context?.adminConfig?.mall?.ad_placements?.account_placement.visual}
                />
            )}
            <Box sx={{ width: `250px` }}>
                {data.map((item, index) => (
                    <Box key={index}>
                        <NavItemButton
                            key={item.name}
                            sx={{ width: `100%` }}
                            className="navItem"
                            active={activeRoute(item.path, theme)}
                            onClick={() => handleNavigation(item.path)}
                        >
                            <item.icon sx={{ ...iconStyle(activeRoute(item.path), theme) }} />
                            <StyledText compact={COMPACT} active={activeRoute(item.path, theme)}>
                                {item.name}
                            </StyledText>
                            <Box mx="auto" />
                            {item.badge && <BadgeValue compact={COMPACT}>{item.badge.value}</BadgeValue>}
                        </NavItemButton>
                    </Box>
                ))}
            </Box>
        </Box>
    );

    return (
        <Box sx={{ verticalAlign: `top`, display: `inline-block`, position: `sticky`, top: `${TOPBARHEIGHT + 24}px` }}>
            {renderLevels(navigations(context))}
        </Box>
    );
};

export default MultiLevelMenu;
